import {
  AdminCreateWebhookSubscription,
  AdminFetchWebhookDeliveries,
  AdminFetchWebhookSubscriptions,
  AdminSendWebhook,
  AdminUpdateWebhookSubscription,
  ClearAdminWebhookDeliveries,
} from 'src/constants/actionTypes'
import type { WebhookDeliveriesPage, WebhookSubscription } from 'src/interfaces/webhook'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { WebhookState } from './types'

const getInitialState = (): WebhookState => {
  return {
    subscriptions: [],
    subscriptionsLoading: false,
    subscriptionsError: null,
    saving: false,
    deliveries: [],
    deliveriesTotal: 0,
    deliveriesLoading: false,
    deliveriesError: null,
    sending: false,
  }
}

export const webhookReducer = (
  state: WebhookState = getInitialState(),
  action: Action<unknown>,
): WebhookState => {
  switch (action.type) {
    case AdminFetchWebhookSubscriptions.type.REQUEST:
      return {
        ...state,
        subscriptions: [],
        subscriptionsLoading: true,
        subscriptionsError: null,
      }
    case AdminFetchWebhookSubscriptions.type.SUCCESS: {
      const subscriptions = (action.payload as WebhookSubscription[] | undefined) ?? []

      return {
        ...state,
        subscriptions,
        subscriptionsLoading: false,
        subscriptionsError: null,
      }
    }
    case AdminFetchWebhookSubscriptions.type.FAILED:
      return {
        ...state,
        subscriptionsLoading: false,
        subscriptionsError: resolveErrorMessage(
          action.payload,
          'Failed to load webhook subscriptions',
        ),
      }

    // Create and update never write to `subscriptions` — the list is refetched on success, which
    // is the single source of truth. This matters more here than elsewhere: the enable/disable
    // toggle sends only `{ isActive }`, and reconciling a partial response into a list by hand is
    // exactly the bug the refetch rule prevents. See the design doc's §4.3.
    case AdminCreateWebhookSubscription.type.REQUEST:
    case AdminUpdateWebhookSubscription.type.REQUEST:
      return {
        ...state,
        saving: true,
      }
    // The plaintext secret from a create is deliberately not stored: see WebhookState.
    case AdminCreateWebhookSubscription.type.SUCCESS:
    case AdminCreateWebhookSubscription.type.FAILED:
    case AdminUpdateWebhookSubscription.type.SUCCESS:
    case AdminUpdateWebhookSubscription.type.FAILED:
      return {
        ...state,
        saving: false,
      }

    // Deliberately does NOT clear `deliveries`: paging through a log that blanks on every page
    // change reads as a fault. GridTable's own loading overlay covers the stale page.
    case AdminFetchWebhookDeliveries.type.REQUEST:
      return {
        ...state,
        deliveriesLoading: true,
        deliveriesError: null,
      }
    case AdminFetchWebhookDeliveries.type.SUCCESS: {
      const page = action.payload as WebhookDeliveriesPage | undefined

      return {
        ...state,
        deliveries: page?.deliveries ?? [],
        deliveriesTotal: page?.total ?? 0,
        deliveriesLoading: false,
        deliveriesError: null,
      }
    }
    case AdminFetchWebhookDeliveries.type.FAILED:
      return {
        ...state,
        deliveriesLoading: false,
        deliveriesError: resolveErrorMessage(action.payload, 'Failed to load webhook deliveries'),
      }
    // The REQUEST above keeps its rows on purpose, so same-customer paging does not blank the
    // grid. That is wrong across customers: without this, the previous customer's deliveries
    // stay on screen while the new request is in flight, and after it fails.
    case ClearAdminWebhookDeliveries.type:
      return {
        ...state,
        deliveries: [],
        deliveriesTotal: 0,
        deliveriesError: null,
      }

    case AdminSendWebhook.type.REQUEST:
      return {
        ...state,
        sending: true,
      }
    case AdminSendWebhook.type.SUCCESS:
    case AdminSendWebhook.type.FAILED:
      return {
        ...state,
        sending: false,
      }

    default:
      return state
  }
}
